import { useState, useEffect, useMemo, useCallback } from "react";
import {
    TablePagination,
    Box,
    Typography,
    FormControl,
    OutlinedInput,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Select,
    MenuItem,
    InputLabel,
    Alert,
    FormHelperText,
    FormControlLabel,
    Checkbox,
    Paper,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddIcon from "@mui/icons-material/Add";
import api from "../../utils/api";
import Table from "../../components/Table";
import { PurchasedCourseColumns } from "./components/PurchasedCourseColumns";
import ManualPurchaseModal from "./components/ManualPurchaseModal";

export default function PurchasedCourses() {
    const [search, setSearch] = useState("");
    const [pagination, setPagination] = useState({
        page: 1,
        perPage: 40,
        total: 0,
        totalPages: 0,
    });
    const [activeFilters, setActiveFilters] = useState({});
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [unassignedRows, setUnassignedRows] = useState([]);
    const [manualPurchaseOpen, setManualPurchaseOpen] = useState(false);

    const [assignPurchase, setAssignPurchase] = useState(null);
    const [classroomOptions, setClassroomOptions] = useState([]);
    const [selectedClassroomId, setSelectedClassroomId] = useState("");
    const [assignLoading, setAssignLoading] = useState(false);
    const [assignError, setAssignError] = useState("");
    const [lessonsPurchase, setLessonsPurchase] = useState(null);
    const [lessonsMode, setLessonsMode] = useState("increase");
    const [lessonsAmount, setLessonsAmount] = useState("1");
    const [lessonsLoading, setLessonsLoading] = useState(false);
    const [lessonsError, setLessonsError] = useState("");
    const [statusPurchase, setStatusPurchase] = useState(null);
    const [statusValue, setStatusValue] = useState("processed");
    const [statusCreateFinanceIncome, setStatusCreateFinanceIncome] = useState(true);
    const [statusLoading, setStatusLoading] = useState(false);
    const [statusError, setStatusError] = useState("");

    const openAssign = useCallback((row) => {
        setAssignPurchase(row);
        setSelectedClassroomId(row.classroom_id ? String(row.classroom_id) : "");
        setAssignError("");
    }, []);

    const openAdjustLessons = useCallback((row) => {
        setLessonsPurchase(row);
        setLessonsMode("increase");
        setLessonsAmount("1");
        setLessonsError("");
    }, []);

    const openChangeStatus = useCallback((row) => {
        setStatusPurchase(row);
        setStatusValue(row?.status || "processed");
        setStatusCreateFinanceIncome(true);
        setStatusError("");
    }, []);

    const handleDeletePurchase = useCallback(async (row) => {
        if (!row?.id) return;
        if (!window.confirm("Delete this purchase and related classroom links?")) return;
        try {
            await api.delete(`admin/purchases/${row.id}`);
            fetchRows(activeFilters, { overridePagination: { page: pagination.page } });
        } catch (err) {
            const msg =
                err.response?.data?.message ||
                (typeof err.response?.data === "string" ? err.response.data : null) ||
                "Could not delete purchase.";
            window.alert(msg);
        }
    }, [activeFilters, pagination.page]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleRemoveFromClassroom = useCallback(async (row) => {
        if (!row?.id || !row?.classroom_id) return;
        const label = row.classroom_name?.trim() || `Classroom #${row.classroom_id}`;
        if (!window.confirm(`Remove this student from "${label}"?`)) return;
        try {
            await api.delete(`admin/purchases/${row.id}/classroom`);
            fetchRows(activeFilters, { overridePagination: { page: pagination.page } });
        } catch (err) {
            const msg =
                err.response?.data?.message ||
                (typeof err.response?.data === "string" ? err.response.data : null) ||
                "Could not remove student from classroom.";
            window.alert(msg);
        }
    }, [activeFilters, pagination.page]); // eslint-disable-line react-hooks/exhaustive-deps

    const columns = useMemo(
        () => PurchasedCourseColumns({
            onAssignClassroom: openAssign,
            onAdjustLessons: openAdjustLessons,
            onChangeStatus: openChangeStatus,
            onDeletePurchase: handleDeletePurchase,
            onRemoveFromClassroom: handleRemoveFromClassroom,
        }),
        [openAssign, openAdjustLessons, openChangeStatus, handleDeletePurchase, handleRemoveFromClassroom]
    );

    const fetchRows = async (filters = activeFilters, options = {}) => {
        const { resetPage = false, overridePagination = {} } = options;

        const currentPage = resetPage ? 1 : overridePagination.page ?? pagination.page;
        const currentPerPage = overridePagination.perPage ?? pagination.perPage;

        setLoading(true);
        try {
            const response = await api.get("admin/purchased-courses", {
                params: {
                    filter: filters,
                    page: currentPage,
                    perPage: currentPerPage,
                },
            });

            setPagination({
                page: currentPage,
                perPage: currentPerPage,
                total: response.data.meta.pagination.total,
                totalPages: response.data.meta.pagination.totalPages,
            });

            setRows(response.data.data || []);
            setUnassignedRows(response.data.unassigned || []);
        } catch (error) {
            console.error("Fetch failed", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!assignPurchase) return;
        let cancelled = false;

        (async () => {
            try {
                const res = await api.get("admin/classrooms", { params: { page: 1, perPage: 500, include_deleted: false } });
                if (!cancelled) {
                    setClassroomOptions(res.data?.data ?? []);
                }
            } catch (e) {
                if (!cancelled) {
                    setClassroomOptions([]);
                    setAssignError("Could not load classrooms.");
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [assignPurchase]);

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            const newFilters = {};
            if (search && search.trim() !== "") {
                newFilters.search = search.trim();
            }
            setActiveFilters(newFilters);
            fetchRows(newFilters, { resetPage: true });
        }, 500);

        return () => clearTimeout(delayDebounce);
    }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

    const handlePageChange = (newPage) => {
        fetchRows(activeFilters, { overridePagination: { page: newPage + 1 } });
    };

    const handleAssignSubmit = async () => {
        if (!assignPurchase || !selectedClassroomId) return;
        setAssignLoading(true);
        setAssignError("");
        try {
            await api.put(`admin/purchases/${assignPurchase.id}/assign-classroom`, {
                classroom_id: Number(selectedClassroomId),
            });
            setAssignPurchase(null);
            fetchRows(activeFilters, { overridePagination: { page: pagination.page } });
        } catch (err) {
            const msg =
                err.response?.data?.message ||
                (typeof err.response?.data === "string" ? err.response.data : null) ||
                "Assignment failed.";
            setAssignError(msg);
        } finally {
            setAssignLoading(false);
        }
    };

    const handleAdjustLessonsSubmit = async () => {
        if (!lessonsPurchase) return;
        const numericAmount = Number(lessonsAmount);
        if (!Number.isInteger(numericAmount) || numericAmount <= 0) {
            setLessonsError("Please enter a positive whole number.");
            return;
        }

        setLessonsLoading(true);
        setLessonsError("");
        try {
            await api.put(`admin/purchases/${lessonsPurchase.id}/lessons`, {
                delta: lessonsMode === "increase" ? numericAmount : -numericAmount,
            });
            setLessonsPurchase(null);
            fetchRows(activeFilters, { overridePagination: { page: pagination.page } });
        } catch (err) {
            const msg =
                err.response?.data?.message ||
                (typeof err.response?.data === "string" ? err.response.data : null) ||
                "Could not update lessons.";
            setLessonsError(msg);
        } finally {
            setLessonsLoading(false);
        }
    };

    const statusesRequiringDetach = ["pending", "canceled", "expired"];
    const statusRequiresDetach = statusesRequiringDetach.includes(statusValue)
        && Boolean(statusPurchase?.classroom_id);

    const handleStatusSubmit = async () => {
        if (!statusPurchase?.id || !statusValue) return;

        if (statusRequiresDetach) {
            setStatusError("Remove the student from the classroom before changing this purchase status.");
            return;
        }

        setStatusLoading(true);
        setStatusError("");
        try {
            const payload = {
                status: statusValue,
            };
            if (statusValue === "processed") {
                payload.create_finance_income = statusCreateFinanceIncome;
            }
            await api.put(`admin/purchases/${statusPurchase.id}/status`, payload);
            setStatusPurchase(null);
            fetchRows(activeFilters, { overridePagination: { page: pagination.page } });
        } catch (err) {
            const msg =
                err.response?.data?.message ||
                (typeof err.response?.data === "string" ? err.response.data : null) ||
                "Could not update status.";
            setStatusError(msg);
        } finally {
            setStatusLoading(false);
        }
    };

    return (
        <Box>
            <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
                Purchased Courses
            </Typography>

            <Box sx={{ display: "flex", mb: 2, justifyContent: "space-between", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
                <Box sx={{ width: { xs: "100%", md: "45ch" } }}>
                    <FormControl sx={{ width: "100%" }} variant="outlined" size="small">
                        <OutlinedInput
                            size="small"
                            placeholder="Search by user or course..."
                            sx={{ background: "#fff" }}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            startAdornment={
                                <InputAdornment position="start">
                                    <SearchRoundedIcon fontSize="small" />
                                </InputAdornment>
                            }
                        />
                    </FormControl>
                </Box>
                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setManualPurchaseOpen(true)}>
                    Add purchase
                </Button>
            </Box>

            {unassignedRows.length > 0 ? (
                <Paper
                    elevation={0}
                    sx={{
                        mb: 3,
                        p: 2,
                        border: "1px solid",
                        borderColor: "warning.light",
                        bgcolor: "rgba(255, 193, 7, 0.06)",
                        borderRadius: 1,
                    }}
                >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                        Unassigned purchases ({unassignedRows.length})
                    </Typography>
                    <Table
                        loading={loading}
                        rows={unassignedRows}
                        columns={columns}
                        selactable={false}
                        draggable={false}
                    />
                </Paper>
            ) : null}

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                Assigned purchases
            </Typography>

            <Table
                loading={loading}
                rows={rows}
                columns={columns}
                selactable={false}
                draggable={false}
            />

            <TablePagination
                component="div"
                count={pagination.total}
                page={pagination.page - 1}
                rowsPerPage={pagination.perPage}
                onPageChange={(event, newPage) => handlePageChange(newPage)}
                rowsPerPageOptions={[]}
            />

            <ManualPurchaseModal
                open={manualPurchaseOpen}
                onClose={(refetch) => {
                    setManualPurchaseOpen(false);
                    if (refetch) {
                        fetchRows(activeFilters, { overridePagination: { page: pagination.page } });
                    }
                }}
            />

            <Dialog open={Boolean(assignPurchase)} onClose={() => !assignLoading && setAssignPurchase(null)} fullWidth maxWidth="sm">
                <DialogTitle>
                    {assignPurchase?.classroom_id ? "Reassign student to a different classroom" : "Assign purchase to classroom"}
                </DialogTitle>
                <DialogContent>
                    {assignPurchase?.classroom_id ? (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Currently in{" "}
                            <strong>
                                {assignPurchase.classroom_name?.trim()
                                    ? assignPurchase.classroom_name
                                    : `Classroom #${assignPurchase.classroom_id}`}
                            </strong>
                            . Saving will remove the student from there and enroll them in the selected classroom.
                        </Alert>
                    ) : null}
                    {assignError ? (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {assignError}
                        </Alert>
                    ) : null}
                    <FormControl fullWidth sx={{ mt: 1 }} size="small">
                        <InputLabel id="assign-classroom-label">Classroom</InputLabel>
                        <Select
                            labelId="assign-classroom-label"
                            label="Classroom"
                            value={selectedClassroomId}
                            onChange={(e) => setSelectedClassroomId(e.target.value)}
                        >
                            {classroomOptions.map((c) => (
                                <MenuItem key={c.id} value={String(c.id)}>
                                    {c.name?.trim()
                                        ? c.name
                                        : (c.classroom_name?.trim() ? c.classroom_name : `Classroom #${c.id}`)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignPurchase(null)} disabled={assignLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAssignSubmit}
                        disabled={
                            assignLoading ||
                            !selectedClassroomId ||
                            String(assignPurchase?.classroom_id ?? "") === String(selectedClassroomId)
                        }
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(lessonsPurchase)} onClose={() => !lessonsLoading && setLessonsPurchase(null)} fullWidth maxWidth="sm">
                <DialogTitle>Adjust lessons</DialogTitle>
                <DialogContent>
                    {lessonsError ? (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {lessonsError}
                        </Alert>
                    ) : null}
                    <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                        <FormControl size="small" fullWidth>
                            <InputLabel id="adjust-lessons-mode-label">Action</InputLabel>
                            <Select
                                labelId="adjust-lessons-mode-label"
                                label="Action"
                                value={lessonsMode}
                                onChange={(e) => setLessonsMode(e.target.value)}
                            >
                                <MenuItem value="increase">Increase</MenuItem>
                                <MenuItem value="decrease">Decrease</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" fullWidth error={Boolean(lessonsError && (!lessonsAmount || Number(lessonsAmount) <= 0))}>
                            <OutlinedInput
                                value={lessonsAmount}
                                onChange={(e) => setLessonsAmount(e.target.value)}
                                type="number"
                                inputProps={{ min: 1, step: 1 }}
                                placeholder="Lessons count"
                            />
                            <FormHelperText>
                                This will update both total and remaining lessons.
                            </FormHelperText>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLessonsPurchase(null)} disabled={lessonsLoading}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleAdjustLessonsSubmit} disabled={lessonsLoading}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(statusPurchase)} onClose={() => !statusLoading && setStatusPurchase(null)} fullWidth maxWidth="sm">
                <DialogTitle>Change purchase status</DialogTitle>
                <DialogContent>
                    {statusRequiresDetach ? (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            This purchase is assigned to{" "}
                            <strong>
                                {statusPurchase.classroom_name?.trim()
                                    ? statusPurchase.classroom_name
                                    : `Classroom #${statusPurchase.classroom_id}`}
                            </strong>
                            . Remove the student from the classroom first, then change the status.
                        </Alert>
                    ) : null}
                    {statusError ? (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {statusError}
                        </Alert>
                    ) : null}
                    <FormControl fullWidth sx={{ mt: 1 }} size="small">
                        <InputLabel id="purchase-status-label">Status</InputLabel>
                        <Select
                            labelId="purchase-status-label"
                            label="Status"
                            value={statusValue}
                            onChange={(e) => {
                                setStatusValue(e.target.value);
                                setStatusError("");
                            }}
                        >
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="processed">Processed</MenuItem>
                            <MenuItem value="canceled">Canceled</MenuItem>
                            <MenuItem value="expired">Expired</MenuItem>
                        </Select>
                    </FormControl>
                    {statusValue === "processed" ? (
                        <FormControl fullWidth sx={{ mt: 1 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={statusCreateFinanceIncome}
                                        onChange={(e) => setStatusCreateFinanceIncome(e.target.checked)}
                                    />
                                }
                                label="Create finance income"
                            />
                        </FormControl>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStatusPurchase(null)} disabled={statusLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleStatusSubmit}
                        disabled={
                            statusLoading
                            || !statusValue
                            || statusRequiresDetach
                        }
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
